'use client';

import { useId, useState, type ChangeEvent, type FormEvent } from 'react';
import {
  CONTACT_TEXTS,
  contactBody,
  EMPTY_CONTACT_MESSAGE,
  isValidEmail,
  missingContactMessageFields,
  postContact,
  type ContactCardData,
  type ContactMessageFields,
} from '@/app/lib/guideChat/contact';

const inputCls = 'border border-gray-300 rounded px-2 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-emerald-400';
const labelCls = 'block text-xs text-gray-500 mb-0.5';

const FIELDS: { field: keyof Omit<ContactMessageFields, 'message'>; type: string; autoComplete: string }[] = [
  { field: 'firstName', type: 'text', autoComplete: 'given-name' },
  { field: 'lastName', type: 'text', autoComplete: 'family-name' },
  { field: 'email', type: 'email', autoComplete: 'email' },
];

// The hand-over of the products without a CRM callback task: the visitor writes to the product's advisers, the conversation travels with the message.
export default function ContactCard({ card, compact = false }: { card: ContactCardData; compact?: boolean }) {
  const id = useId();
  const question = typeof card.handover.question === 'string' ? card.handover.question : '';
  const [fields, setFields] = useState<ContactMessageFields>({ ...EMPTY_CONTACT_MESSAGE, message: question });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<'form' | 'sending' | 'done'>('form');
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const update = (field: keyof ContactMessageFields) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFields((f) => ({ ...f, [field]: e.target.value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (status === 'sending') return;
    if (missingContactMessageFields(fields).length > 0) {
      setError(CONTACT_TEXTS.missingFields);
      return;
    }
    if (!isValidEmail(fields.email)) {
      setError(CONTACT_TEXTS.invalidEmail);
      return;
    }
    if (!consent) {
      setError(CONTACT_TEXTS.missingConsent);
      return;
    }
    setError(null);
    setStatus('sending');
    const result = await postContact(contactBody(fields, card.product, card.handover));
    if (!result.ok) {
      setError(result.error);
      setStatus('form');
      return;
    }
    setConfirmation(result.confirmation ?? card.confirmation ?? CONTACT_TEXTS.confirmed);
    setStatus('done');
  }

  if (status === 'done') {
    return (
      <div className="mt-1 w-full bg-white border border-emerald-200 rounded-xl p-3 shadow-sm" role="status" data-contact-sent>
        <div className="text-sm font-semibold text-emerald-800 mb-1">{CONTACT_TEXTS.doneTitle}</div>
        <p className="text-xs text-gray-700">{confirmation}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-1 w-full bg-white border border-gray-200 rounded-xl p-3 shadow-sm" data-contact-card>
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <span className="rounded border px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800 border-emerald-200">{CONTACT_TEXTS.badge}</span>
        {!compact && card.nonAnswerReasonLabel && <span className="text-xs text-gray-400">{card.nonAnswerReasonLabel}</span>}
      </div>
      {card.title && <div className="text-sm font-semibold text-gray-800">{card.title}</div>}
      {card.message && <p className="text-sm text-gray-600 mt-0.5">{card.message}</p>}

      <div className="mt-3 grid grid-cols-2 gap-2">
        {FIELDS.map(({ field, type, autoComplete }) => (
          <div key={field} className={field === 'email' ? 'col-span-2' : undefined}>
            <label htmlFor={`${id}-${field}`} className={labelCls}>
              {CONTACT_TEXTS[field]}
            </label>
            <input
              id={`${id}-${field}`}
              name={field}
              type={type}
              autoComplete={autoComplete}
              value={fields[field]}
              onChange={update(field)}
              className={inputCls}
            />
          </div>
        ))}
      </div>
      <div className="mt-2">
        <label htmlFor={`${id}-message`} className={labelCls}>
          {CONTACT_TEXTS.message}
        </label>
        <textarea
          id={`${id}-message`}
          name="message"
          rows={3}
          value={fields.message}
          onChange={update('message')}
          placeholder={card.messagePlaceholder ?? CONTACT_TEXTS.messagePlaceholder}
          className={`${inputCls} resize-none`}
        />
      </div>

      <label className="mt-2 flex items-start gap-2 text-xs text-gray-600">
        <input type="checkbox" name="consent" className="mt-0.5 shrink-0" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>{CONTACT_TEXTS.consent}</span>
      </label>

      {error && <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

      <button
        type="submit"
        disabled={status === 'sending'}
        className="mt-3 w-full rounded-lg bg-emerald-600 text-white font-medium py-2 disabled:opacity-50 hover:bg-emerald-700"
      >
        {status === 'sending' ? CONTACT_TEXTS.sending : card.submitLabel ?? CONTACT_TEXTS.submit}
      </button>
    </form>
  );
}
